import React, { useEffect, useState } from "react";

import ElasticSearchAPIConnector from "@elastic/search-ui-elasticsearch-connector";
// import moment from "moment";

import {
  ErrorBoundary,
  Facet,
  SearchProvider,
  SearchBox,
  Results,
  PagingInfo,
  ResultsPerPage,
  Paging,
  Sorting,
  WithSearch
} from "@elastic/react-search-ui";
import {
  Layout
} from "@elastic/react-search-ui-views";

import CustomPaginationView from "@scripts/frontend/job-listing/components/pagination/CustomPaginationView";

import FacetContainer from "./customFacet";
// Common Elasticsearch API settings
const ELASTIC_CONFIG = {
  host: "https://5d0493d4cacf44dbb8a0b25eefe15970.us-east-2.aws.elastic-cloud.com:443",
  apiKey: "Wnl4NzBaUUJCNlFhVGdHek1lQUo6WVdRSy1pSGJUWENvQkxZekd1NC0wQQ==",
  jobsIndex: "aq-na-jobs-v9-north-america",
  marketsIndex: "markets-north-america-v2",
};

// Elasticsearch Connector for job listings
const connector = new ElasticSearchAPIConnector({
  host: ELASTIC_CONFIG.host,
  index: ELASTIC_CONFIG.jobsIndex,
  apiKey: ELASTIC_CONFIG.apiKey,
});

const config = {
  searchQuery: {
    search_fields: {
      job_title: {
        weight: 3
      },
      job_description: {},
      location: {},
      client_description: {}
    },
    result_fields: {
      job_title: {
        snippet: {}
      },
      job_description: {
        snippet: {}
      },
      location: {
        raw: {}
      },
      salary: {
        raw: {}
      },
      job_id: {
        raw: {}
      },
      posted_date: {
        raw: {}
      }
    },
    filters: [
      // {
      //   field: "markets",
      //   values: [
      //     "United Kingdom",
      //     "Fukuoka",
      //     "Osaka",
      //     "Tokyo",
      //     "Nagoya",
      //     "Melbourne⁠ | Narrm",
      //     "Sydney",
      //     "Amsterdam",
      //     "Germany",
      //     "Paris"
      //   ],
      //   type: "none"
      // },
      {
        field: "offsite_preference.keyword",
        values: ["1", "2", "3", "4"],
        type: "none"
      }
    ],
    disjunctiveFacets: [
      "markets.keyword",
      "segments.keyword",
      "placement_types.keyword",
      "offsite_preference.keyword"
    ],
    facets: {
      "markets.keyword": { type: "value" },
      "segments.keyword": { type: "value", size: 1000 },
      "placement_types.keyword": { type: "value" },
      "offsite_preference.keyword": { type: "value" }
    }
  },
  autocompleteQuery: {
    results: {
      resultsPerPage: 5,
      search_fields: {
        job_title: {
          weight: 3
        }
      },
      result_fields: {
        job_title: {
          snippet: {
            size: 100,
            fallback: true
          }
        }
      }
    }
  },
  apiConnector: connector,
  alwaysSearchOnInitialLoad: true
};

// Fetch Market from North America Index
const fetchMarkets = async () => {
  const response = await fetch(`${ELASTIC_CONFIG.host}/${ELASTIC_CONFIG.marketsIndex}/_search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `ApiKey ${ELASTIC_CONFIG.apiKey}`
    },
    body: JSON.stringify({
      size: 1000,
      _source: ["name"]
    })
  });

  const data = await response.json();
  return data.hits.hits.map((hit) => hit._source.name);
};

// Fetch Job Counts by Market
const fetchJobCountsByMarket = async () => {
  const response = await fetch(`${ELASTIC_CONFIG.host}/${ELASTIC_CONFIG.jobsIndex}/_search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `ApiKey ${ELASTIC_CONFIG.apiKey}`
    },
    body: JSON.stringify({
      size: 0,
      aggs: {
        markets_count: {
          terms: { field: "markets.keyword", size: 1000 }
        }
      }
    })
  });

  const data = await response.json();
  return data.aggregations?.markets_count?.buckets.reduce((acc, bucket) => {
    acc[bucket.key] = bucket.doc_count;
    return acc;
  }, {});
};

// Fetch and Combine Market Data
const getMarketsWithJobCounts = async () => {
  const markets = await fetchMarkets();
  const jobCounts = await fetchJobCountsByMarket();

  return markets.map((market) => ({
    name: market,
    jobCount: jobCounts[market] || 0
  }));
};

const CustomResultView = ({ result, onClickLink }) => {
  if (!result) return null;

  const jobId = result.job_id?.raw || "";
  const jobTitle = result.job_title?.raw || "";
  const location = result.location?.raw || "";
  const postedDate = result.posted_date?.raw || "";
  const homeUrl = document.getElementById('listing-main')?.getAttribute('data-home-url') || '';
  const jobBaseUrl = document.getElementById('listing-main')?.getAttribute('data-job-base-url') || '';
  // Format the date
  const formattedDate = postedDate
    ? new Date(postedDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
    // Check if the job is new (posted within the last 7 days)
    const isNewJob = (postedDate) => {
      const postedDateObj = new Date(postedDate);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      return postedDateObj >= sevenDaysAgo;
    };

  return (
    <a
      href={`/find-work/${jobId}`}
      title={jobTitle.replace(/<\/?em>/g, "")}
      className="job-card"
    >
      <div
        className="job-card__block"
        role="region"
        aria-labelledby={`job-title-${jobId}`}
      >
        <h2 className="job-card__title" id={`job-title-${jobId}`}>
          {jobTitle.replace(/<\/?em>/g, "")}
        </h2>
        <p className="job-card__location">{location}</p>
        <time className={`job-card__posted ${isNewJob(result.posted_date?.raw) ? "job-card__posted--new" : ""}`} itemProp="datePublished">
          Posted {formattedDate}
        </time>
      </div>
    </a>
  );

};

const JobListingMain = () => {
  const [marketFacets, setMarketFacets] = useState([]);

  useEffect(() => {
    getMarketsWithJobCounts().then(setMarketFacets);
  
  }, []);
  return (
    <SearchProvider config={config}>
      <WithSearch mapContextToProps={({ resultsPerPage, setResultsPerPage, setSearchTerm }) => ({
        resultsPerPage, setResultsPerPage, setSearchTerm
      })}>
        {({ resultsPerPage, setResultsPerPage, setSearchTerm }) => {
          React.useEffect(() => {
            if (resultsPerPage !== 16) {
              setResultsPerPage(16);
            }
          }, [resultsPerPage, setResultsPerPage]);

          return (
            <div className="App">
              <ErrorBoundary>
                <Layout
                  header={
                    <div className="sui-filter-bar-content filter-bar__content">
                        <div className="filter-bar__combined-menu" id="filter-bar__combined-menu">
                          <a className="filter-bar__combined-menu__a erase-trace" href="#filter-bar__menu">
                          <span className="filter-bar__text-label-mobile">Filters</span>
                            <span className="filter-bar__filter-icon"></span>
                          </a>
                        </div>

                        <div className="filter-bar__menu filter-bar__menu--job" id="filter-bar__menu">
                          <div className="filter-bar__header-mobile">
                            <span className="filter-bar__header-mobile__text">Filters </span>
                            <a className="filter-bar__header-mobile__close-icon erase-trace" href="#filter-bar__content" aria-label="close"></a>
                          </div>
                          {/* <div className="sui-accordion">
                            <details className="sui-details">
                              <summary className="sui-summary">Location <span className="summary-icon"></span></summary>
                              <fieldset className="sui-facet">
                                <div className="sui-multi-checkbox-facet">
                                  {marketFacets.map((facet) => {
                                    const isChecked = JSON.parse(localStorage.getItem("selectedMarkets") || "[]").includes(facet.name);

                                    return (
                                      <label
                                        key={facet.name}
                                        htmlFor={`facet_${facet.name}`}
                                        className="sui-multi-checkbox-facet__option-label"
                                      >
                                        <div className="sui-multi-checkbox-facet__option-input-wrapper">
                                          <input
                                            data-transaction-name="facet - Location"
                                            id={`facet_${facet.name}`}
                                            type="checkbox"
                                            className="sui-multi-checkbox-facet__checkbox"
                                            value={facet.name}
                                            checked={isChecked}
                                            onChange={(e) => {
                                              const selectedMarket = e.target.value;
                                              const url = new URL(window.location);
                                              let params = new URLSearchParams(url.search);
                                              const filterField = "filters[0][field]";
                                              const filterValues = "filters[0][values]";
                                              const filterType = "filters[0][type]";

                                              let selectedMarkets = JSON.parse(localStorage.getItem("selectedMarkets")) || [];

                                              if (e.target.checked) {
                                                if (!selectedMarkets.includes(selectedMarket)) {
                                                  selectedMarkets.push(selectedMarket);
                                                }
                                              } else {
                                                selectedMarkets = selectedMarkets.filter((market) => market !== selectedMarket);
                                              }

                                              localStorage.setItem("selectedMarkets", JSON.stringify(selectedMarkets));

                                              params.delete(filterField);
                                              params.delete(filterType);
                                              selectedMarkets.forEach((_, index) => params.delete(`${filterValues}[${index}]`));

                                              if (selectedMarkets.length > 0) {
                                                params.set(filterField, "markets.keyword");
                                                params.set(filterType, "all");
                                                selectedMarkets.forEach((market, index) => {
                                                  params.set(`${filterValues}[${index}]`, market);
                                                });
                                              }

                                              if (url.searchParams.has("size")) {
                                                params.set("size", url.searchParams.get("size"));
                                              }

                                              const newUrl = selectedMarkets.length > 0 ? `${url.pathname}?${params.toString()}` : url.pathname;
                                              window.history.replaceState({}, "", newUrl);
                                              window.location.href = newUrl;
                                            }}
                                          />
                                          <div className="checkbox__checkmark"></div>
                                          <span className="sui-multi-checkbox-facet__input-text">{facet.name} <span className="sui-multi-checkbox-facet__input-count">({facet.jobCount})</span></span>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </fieldset>
                            </details>
                          </div> */}
                          <FacetContainer key={"1"} field={"markets.keyword"} label={"Markets"} />
                          <FacetContainer key={"2"} field={"placement_types.keyword"} label={"Placement Types"} />
                          <FacetContainer key={"3"} field={"segments.keyword"} label={"Segments"} />
                          <FacetContainer key={"4"} field={"offsite_preference.keyword"} label={"Offsite Preference"} />
                        </div>

                        <div className="filter-bar__dropdown-buttons-mobile">
                          <a className="tertiary-info erase-trace" href="https://aquent.com/find-work">
                            Clear all
                          </a>
                          <div className="wp-block-button is-style-dark-orange">
                            <button
                              className="wp-block-button__link"
                              onClick={() => {
                                window.location.replace("#filter-bar__content");
                                document.getElementById("jobs-filter")?.submit();
                              }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>

                        <div className="filter-bar__search filter-bar__search-job">
                          <div className="filter-bar__search-field">
                            <SearchBox
                            autocompleteMinimumCharacters={3}
                            autocompleteResults={{
                              sectionTitle: "Results",
                              titleField: "job_title",
                              shouldTrackClickThrough: true
                            }}
                            autocompleteSuggestions={true}
                            inputProps={{ placeholder: "Search jobs and keywords" }}
                            onSelect={(selection) => {
                              if (selection && selection.job_title && selection.job_title.snippet) {
                                let selectedJobTitle = selection.job_title.snippet[0];
                                selectedJobTitle = selectedJobTitle.replace(/<\/?em>/g, "");
                                setSearchTerm(selectedJobTitle, { shouldClearFilters: true });
                              }
                            }}
                          />
                          </div>

                          <div className="filter-bar__search-icon" data-aria-label-open="Open Search" data-aria-label-close="Close Search">
                            <div className="filter-bar__search-icon__glass"></div>
                            <a className="filter-bar__search-icon__a erase-trace" href="#filter-bar__combined-menu" id="open-search" aria-label="open">
                              <span className="hidden">Open Search</span>
                            </a>
                            <a className="filter-bar__search-icon__a erase-trace" href="#filter-bar__content" id="close-search" aria-label="close">
                              <span className="hidden">Close Search</span>
                            </a>
                          </div>
                        </div>
                   </div>
                   
                  }
                  sideContent={
                    <div>
                      {/* <div className="sui-accordion">
                        <details className="sui-details">
                          <summary className="sui-summary">Location <span className="summary-icon"></span></summary>
                          <fieldset className="sui-facet">
                            <div className="sui-multi-checkbox-facet">
                              {marketFacets.map((facet) => {
                                const isChecked = JSON.parse(localStorage.getItem("selectedMarkets") || "[]").includes(facet.name);

                                return (
                                  <label
                                    key={facet.name}
                                    htmlFor={`facet_${facet.name}`}
                                    className="sui-multi-checkbox-facet__option-label"
                                  >
                                    <div className="sui-multi-checkbox-facet__option-input-wrapper">
                                      <input
                                        data-transaction-name="facet - Location"
                                        id={`facet_${facet.name}`}
                                        type="checkbox"
                                        className="sui-multi-checkbox-facet__checkbox"
                                        value={facet.name}
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const selectedMarket = e.target.value;
                                          const url = new URL(window.location);
                                          let params = new URLSearchParams(url.search);
                                          const filterField = "filters[0][field]";
                                          const filterValues = "filters[0][values]";
                                          const filterType = "filters[0][type]";

                                          let selectedMarkets = JSON.parse(localStorage.getItem("selectedMarkets")) || [];

                                          if (e.target.checked) {
                                            if (!selectedMarkets.includes(selectedMarket)) {
                                              selectedMarkets.push(selectedMarket);
                                            }
                                          } else {
                                            selectedMarkets = selectedMarkets.filter((market) => market !== selectedMarket);
                                          }

                                          localStorage.setItem("selectedMarkets", JSON.stringify(selectedMarkets));

                                          params.delete(filterField);
                                          params.delete(filterType);
                                          selectedMarkets.forEach((_, index) => params.delete(`${filterValues}[${index}]`));

                                          if (selectedMarkets.length > 0) {
                                            params.set(filterField, "markets.keyword");
                                            params.set(filterType, "all");
                                            selectedMarkets.forEach((market, index) => {
                                              params.set(`${filterValues}[${index}]`, market);
                                            });
                                          }

                                          if (url.searchParams.has("size")) {
                                            params.set("size", url.searchParams.get("size"));
                                          }

                                          const newUrl = selectedMarkets.length > 0 ? `${url.pathname}?${params.toString()}` : url.pathname;
                                          window.history.replaceState({}, "", newUrl);
                                          window.location.href = newUrl;
                                        }}
                                      />
                                      <div className="checkbox__checkmark"></div>
                                      <span className="sui-multi-checkbox-facet__input-text">{facet.name} <span className="sui-multi-checkbox-facet__input-count">({facet.jobCount})</span></span>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </fieldset>
                        </details>
                      </div> */}
                      <FacetContainer key={"1"} field={"markets.keyword"} label={"Markets"}  />
                      <FacetContainer key={"2"} field={"placement_types.keyword"} label={"Placement Types"} />
                      <FacetContainer key={"3"} field={"segments.keyword"} label={"Segments"} />
                      <FacetContainer key={"4"} field={"offsite_preference.keyword"} label={"Offsite Preference"} />
                    </div>
                  }
                  bodyContent={
                    <Results
                      resultView={CustomResultView}
                      titleField="job_title"
                      urlField="job_id"
                      shouldTrackClickThrough={true}
                    />
                  }
                  bodyHeader={
                    <>
                      <PagingInfo />
                      <ResultsPerPage />
                    </>
                  }
                  bodyFooter={
                    <CustomPaginationView pagingContainer="test"/>
                  }
                />
              </ErrorBoundary>
            </div>
          );
        }}
      </WithSearch>
    </SearchProvider>
  );
};

export { JobListingMain };
